import React, { useEffect, useState } from "react";
import { api, money } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { ChevronRight, HardHat, Loader2, DollarSign } from "lucide-react";

const inputCls = "rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";

const ServiceRow = ({ svc, onReload }) => {
  const { t } = useLang();
  const [pct, setPct] = useState(Math.round((svc.progress || 0) * 100));
  const [amount, setAmount] = useState("");
  const [period, setPeriod] = useState("");
  const [busy, setBusy] = useState(false);

  const saveProgress = async (val) => {
    setPct(val);
    try { await api.put(`/estimates/${svc.estimate_id}/subcontracts/${svc.sub_id}`, { progress: val / 100 }); onReload(); }
    catch { toast.error(t("error")); }
  };
  const pay = async (amt) => {
    const value = parseFloat(amt);
    if (!value || value <= 0) return toast.error(t("amount"));
    setBusy(true);
    try {
      await api.post(`/estimates/${svc.estimate_id}/subcontracts/${svc.sub_id}/payment`, { amount: value, period });
      toast.success(t("generatePayment"));
      setAmount(""); setPeriod(""); onReload();
    } catch { toast.error(t("error")); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`pay-service-${svc.sub_id}`} className="border border-slate-200 rounded-lg p-3 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-medium text-slate-900 text-sm">{svc.service}</div>
          <div className="text-xs text-slate-400">{svc.ticket_number || svc.estimate_number} · {svc.unit_name || svc.title}</div>
        </div>
        <div className="text-right text-sm">
          <div className="text-xs text-slate-400">{t("contractValue")}</div>
          <div className="font-semibold tabular-nums">{money(svc.value)}</div>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-3">
        <span className="text-xs text-slate-500 w-24">{t("percentComplete")}</span>
        <input data-testid={`pct-range-${svc.sub_id}`} type="range" min={0} max={100} value={pct} onChange={(e) => setPct(Number(e.target.value))} onMouseUp={(e) => saveProgress(Number(e.target.value))} onTouchEnd={(e) => saveProgress(Number(e.target.value))} className="flex-1 accent-slate-900" />
        <span className="text-sm font-semibold tabular-nums w-12 text-right">{pct}%</span>
      </div>

      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm tabular-nums bg-slate-50 rounded-lg p-2.5">
        <div><div className="text-[10px] uppercase text-slate-400">{t("earned")}</div><div className="font-semibold">{money(svc.earned)}</div></div>
        <div><div className="text-[10px] uppercase text-slate-400">{t("paid")}</div><div className="font-semibold">{money(svc.paid)}</div></div>
        <div><div className="text-[10px] uppercase text-slate-400">{t("due")}</div><div className="font-semibold text-fuchsia-700">{money(svc.due)}</div></div>
        <div><div className="text-[10px] uppercase text-slate-400">{t("remaining")}</div><div className="font-semibold text-slate-500">{money(svc.value - svc.paid)}</div></div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input data-testid={`pay-period-${svc.sub_id}`} value={period} onChange={(e) => setPeriod(e.target.value)} placeholder={t("period")} className={`${inputCls} w-36`} />
        <input data-testid={`pay-amount-${svc.sub_id}`} type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={t("amount")} className={`${inputCls} w-28`} />
        <button data-testid={`pay-manual-${svc.sub_id}`} disabled={busy} onClick={() => pay(amount)} className="text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg px-3 py-1.5 disabled:opacity-60">{t("generatePayment")}</button>
        <button data-testid={`pay-due-${svc.sub_id}`} disabled={busy || svc.due <= 0} onClick={() => pay(svc.due)} className="inline-flex items-center gap-1 text-xs font-medium bg-fuchsia-600 text-white rounded-lg px-3 py-1.5 hover:bg-fuchsia-700 disabled:opacity-40">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <DollarSign className="w-3.5 h-3.5" />}{t("payDue")} ({money(svc.due)})
        </button>
      </div>

      {(svc.payments || []).length > 0 && (
        <div className="mt-2 pt-2 border-t border-slate-100 space-y-1">
          {svc.payments.map((p) => (
            <div key={p.id} className="flex justify-between text-xs text-slate-500"><span>{p.period || p.date?.slice(0, 10)}</span><span className="tabular-nums">{money(p.amount)}</span></div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function SubPayments() {
  const { t } = useLang();
  const [groups, setGroups] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const load = () => api.get("/subcontractor-payments").then((r) => setGroups(r.groups || []));
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5">
      <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{t("payments")}</h1>

      <div className="space-y-3">
        {groups.map((g, i) => {
          const key = g.subcontractor_id || `name-${i}`;
          const isOpen = expanded === key;
          return (
            <div key={key} data-testid={`pay-group-${key}`} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
              <button onClick={() => setExpanded(isOpen ? null : key)} className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-3">
                  <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                  <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center text-white"><HardHat className="w-4 h-4" /></div>
                  <div className="text-left">
                    <div className="font-semibold text-slate-900">{g.name}</div>
                    <div className="text-xs text-slate-400">{g.specialty} · {g.services.length} {t("services")}</div>
                  </div>
                </div>
                <div className="flex items-center gap-6 text-sm tabular-nums">
                  <div className="text-right hidden sm:block"><div className="text-[10px] uppercase text-slate-400">{t("earned")}</div><div className="font-semibold">{money(g.earned)}</div></div>
                  <div className="text-right hidden sm:block"><div className="text-[10px] uppercase text-slate-400">{t("paid")}</div><div className="font-semibold">{money(g.paid)}</div></div>
                  <div className="text-right"><div className="text-[10px] uppercase text-slate-400">{t("due")}</div><div className="font-bold text-fuchsia-700">{money(g.due)}</div></div>
                </div>
              </button>
              {isOpen && (
                <div className="px-5 pb-5 pt-1 space-y-3 bg-slate-50/60">
                  {g.services.map((svc) => <ServiceRow key={svc.sub_id} svc={svc} onReload={load} />)}
                </div>
              )}
            </div>
          );
        })}
        {groups.length === 0 && <p className="text-sm text-slate-400 py-12 text-center">{t("noSubPayments")}</p>}
      </div>
    </div>
  );
}
